<template>
  <div class="relative setting-audience-authentication">
    <div class="overlay-disable-setting" v-if="isDisableEditPresentation"></div>
    <div
      class="absolute top-0 left-0 w-full h-full z-10 cursor-pointer"
      v-if="planBenefit.privacyControl && !planBenefit.privacyControl.participantAuthentication"
      @click="handleCheckNeedUpgradePlan()"
    ></div>
    <div class="relative">
      <aha-checkbox
        id="settings-check-audience-admission"
        @change="handleCheckBoxAudienceAuthentication"
        v-model="presentation.isAudienceAuthentication"
        :disabled="!isOwnerOfPresentation"
        class="mb-3"
        style="font-size: 13.5px"
      >
        <span class="-mr-4 -ml-1">
          {{ $i18nPresenter.t('Require participants to enter authentication code') }}
          <span class="relative">
            <span v-if="!canUseNewTooltip">
              <IconNeedUpgradePlan
                ref="iconNeedUpgradePlanComponentAudienceAuthen"
                :noPermission="planBenefit.privacyControl &&
                  !planBenefit.privacyControl.participantAuthentication"
                :defaultTooltip="$i18nPresenter.t(
                  'Audience must enter the unique codes you provide to be able to join the presentation.'
                )"
                left="-200px"
                :upgradePlan="planBenefit.privacyControl.participantAuthentication_planName"
                featureName="Audience Authentication"
                v-if="planBenefit.interactivePresentation"
              />
            </span>
            <span v-else>
              <UpgradeIcon
                ref="iconNeedUpgradePlanComponentAudienceAuthen"
                :noPermission="planBenefit.privacyControl &&
                  !planBenefit.privacyControl.participantAuthentication"
                :defaultTooltip="$i18nPresenter.t(
                  'Audience must enter the unique codes you provide to be able to join the presentation.'
                )"
                :upgradePlan="planBenefit.privacyControl.participantAuthentication_planName"
                featureName="Audience Authentication"
                v-if="planBenefit.interactivePresentation"
              />
            </span>
          </span>
        </span>
      </aha-checkbox>
    </div>
    <div
      v-if="presentation.isAudienceAuthentication && isOwnerOfPresentation"
    >
      <div class="flex-row flex">
        <aha-input
          type="number"
          class="w-48"
          :placeholder="$i18nPresenter.t('Number of participants')"
          required
          v-model="presentation.numberOfAuthens"
          @change="updateNumberOfAuthen()"
          @input="debounceNumberOfAuthen()"
        />
        <aha-antd-button
          class="ml-2"
          @click="generateListCode()"
          :submitting="isSubmitGenerate"
          isLoadingInline
          :disabled="isSubmitGenerate || !presentation.numberOfAuthens"
        >
          {{ $i18nPresenter.t('Generate code') }}
        </aha-antd-button>
      </div>

      <div class="inline-block">
        <aha-antd-button
          v-aha-emit-action
          name="download-authentication-code-list"
          variant="link"
          @click="downloadFile()"
          class="download-file-link"
        >
          {{ $i18nPresenter.t('Download authentication code list') }}
          <aha-spinner v-if="isSubmitGetList" class="ml-2 block" />
        </aha-antd-button>
      </div>
      <br />
      <div class="inline-block">
        <aha-antd-button
          v-aha-emit-action
          name="delete-generated-codes"
          variant="link"
          @click="isShowDeleteCode = true"
          class="delete-code-link"
        >
          {{ $i18nPresenter.t('Delete generated codes') }}
        </aha-antd-button>
      </div>
    </div>
    <aha-modal
      :title="$i18nPresenter.t('Delete generated codes')"
      id="modal-confirm-delete-generate-code"
      v-if="isShowDeleteCode"
      @hide="isShowDeleteCode = false"
    >
      <div class="overflow-hidden">
        {{ $i18nPresenter.t('All generated codes will no longer be valid after being deleted.') }}
      </div>
      <template #action>
        <aha-link @click="isShowDeleteCode = false">
          <span class="font-bold">{{ $i18nPresenter.t('Cancel') }}</span>
        </aha-link>
        <aha-button
          variant="danger"
          class="mr-2"
          @click="deleteCode()"
          has-margin-left
          :disabled="isSubmitDeleteList"
          :submitting="isSubmitDeleteList"
        >
          {{ $i18nPresenter.t('Delete') }}
        </aha-button>
      </template>
    </aha-modal>
  </div>
</template>

<script>
import _ from 'lodash'

import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import utils from '@/util'
import config from '@/config'

import PresentationApi from '@/api/presentation'
import eventCategories from '@/constant/eventCategories'
import audienceAuthenticationApi from '@/api/audienceAuthentication'
import IconNeedUpgradePlan from '@/components-v2/my-plan/IconNeedUpgradePlan.vue'
import UpgradeIcon from '@/components-v2/my-plan/UpgradeIcon.vue'

export default {
  components: {
    IconNeedUpgradePlan,
    UpgradeIcon,
  },
  data() {
    return {
      numberOfPaticipants: 10,
      isSubmitGenerate: false,
      isSubmitGetList: false,
      isSubmitDeleteList: false,
      isShowDeleteCode: false,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      currentUser: (state) => state.currentUser,
      planBenefit: (state) => state.planBenefit,
    }),
    ...mapGetters('featureToggles', ['canUseNewTooltip']),
    isEURegion() {
      return config.region === 'eu'
    },
    audienceAuthenticationTooltip() {
      return this.$i18nPresenter.t('Participants must enter the unique codes you provide, to be able to join the presentation.')
    },
    isOwnerOfPresentation() {
      if (this.currentUser.id === this.presentation.userId) {
        return true
      }
      return false
    },
  },
  methods: {
    ...mapActions(['setSyncLoadingAction', 'updatePresentation']),

    // eslint-disable-next-line func-names
    debounceNumberOfAuthen: _.debounce(async function () {
      this.updateNumberOfAuthen()
    }, 300),

    async handleCheckBoxAudienceAuthentication() {
      await this.updateAdditionalFields()
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Audience authentication > Audience authentication checkbox',
        `Audience Authentication: ${this.presentation.isAudienceAuthentication}`,
      )
    },
    trackingUpgrade() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Audience authentication > Audience authentication button upgrade',
        'Audience Authentication Click Upgrade',
      )
    },
    async updateAdditionalFields() {
      try {
        this.setSyncLoadingAction(1)
        await PresentationApi.updatePresentationByPresentationId(this.presentation.id, {
          isAudienceAuthentication: this.presentation.isAudienceAuthentication,
        })
        this.updatePresentation(this.presentation)
        this.setSyncLoadingAction(2)
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
      }
    },
    async updateNumberOfAuthen() {
      try {
        if (this.presentation.numberOfAuthens > 2000) {
          this.presentation.numberOfAuthens = 2000
        }
        if (this.presentation.numberOfAuthens < 0 || !this.presentation.numberOfAuthens) {
          this.presentation.numberOfAuthens = 0
        }
        this.setSyncLoadingAction(1)
        await PresentationApi.updatePresentationByPresentationId(this.presentation.id, {
          numberOfAuthens: this.presentation.numberOfAuthens,
        })
        this.updatePresentation(this.presentation)
        this.setSyncLoadingAction(2)
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
      }
    },
    async generateListCode() {
      if (this.isSubmitGenerate) return
      this.isSubmitGenerate = true
      try {
        const respAudiences = await audienceAuthenticationApi.createAudienceAuthentication({
          presentationId: this.presentation.id,
          numberOfPaticipants: parseInt(this.presentation.numberOfAuthens, 10),
        })
        this.getFile(respAudiences)
        utils.showToastSuccess(this.$i18nPresenter.t('Authentication code generated successfully'))
      } catch (e) {
        Sentry.captureException(e)
      }
      this.isSubmitGenerate = false
    },
    async deleteCode() {
      if (this.isSubmitDeleteList) return
      this.isSubmitDeleteList = true
      try {
        await audienceAuthenticationApi.deleteAudienceAuthentication({
          presentationId: this.presentation.id,
        })
        this.isShowDeleteCode = false
        utils.showToastSuccess(this.$i18nPresenter.t('Delete generated code successfully'))
      } catch (e) {
        Sentry.captureException(e)
      }
      this.isSubmitDeleteList = false
    },
    async downloadFile() {
      if (this.isSubmitGetList) return
      this.isSubmitGetList = true
      const resp = await audienceAuthenticationApi.getAudienceAuthentication({
        presentationId: this.presentation.id,
      })
      this.getFile(resp)
      this.isSubmitGetList = false
    },

    getFile(data) {
      const csv = data.map((d) => {
        return `${d.authenticationCode}\n`
      }).toString().replaceAll(',', '')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      // Create a link to download it
      const pom = document.createElement('a')
      pom.href = url
      pom.setAttribute('download', 'ahaslides-participant-code.csv')
      pom.click()
    },
    handleCheckNeedUpgradePlan() {
      if (this.planBenefit.privacyControl && !this.planBenefit.privacyControl.participantAuthentication) {
        this.$refs.iconNeedUpgradePlanComponentAudienceAuthen.toggleTooltip()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay-disable-setting {
  @apply bg-base-0 opacity-50 absolute left-0 top-0 w-full h-full z-20;
}
.setting-audience-authentication::v-deep {
  .aha-checkbox.inline-flex {
    display: inline-flex !important; // overwrite CSS bootstrap
  }
}
.download-file-link {
  @apply cursor-pointer mt-4 flex flex-row hover:text-primary-100 pl-0;
}
.delete-code-link {
  @apply cursor-pointer mt-2 flex flex-row pl-0;
}
</style>
